// ventaCafeteriaService.ts

import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';
import { operatorHeaders, requireOperatorToken } from './pos/operatorGrant';

const API_URL = 'ventas-cafeteria/';

export interface DetalleVentaCafeteria {
    id: number;
    venta: number;
    producto: number;
    producto_info: {
        id: number;
        nombre: string;
        precio: string;
        imagen: string | null;
        controlar_stock: boolean;
        stock: number | null;
    } | null;
    nombre_producto?: string;
    cantidad: number;
    precio_unitario: string;
    monto_descuento?: string;
    subtotal: string;
}

export interface VentaCafeteria {
    id: number;
    sesion_pos: number;
    cliente: number | null;
    cliente_info?: {          // <-- nuevo campo
        first_name: string;
        last_name: string;
        email?: string;
    } | null;
    fecha_venta: string;
    total: string;
    estado_pago: 'pendiente' | 'parcial' | 'pagado' | 'cancelado';
    codigo: string | null;
    observaciones: string;
    tipo_comprobante: 'factura' | 'recibo';
    detalles: DetalleVentaCafeteria[];
    subtotal: string;
    monto_pagado: string;
    saldo_pendiente: string;
    pagos: { id: number; codigo: string; fecha_pago: string; monto: string; tipo_pago: string; efectivo_recibido: string | null; cambio: string | null }[];
    descuento?: string;
    sucursal: number | null;        // ID de sucursal
    sucursal_info?: {              // datos de sucursal
        id: number;
        nombre: string;
    } | null;
}

export interface ItemVenta {
    producto_id: number;
    cantidad: number;
    monto_descuento?: string;
}

export interface PagoVenta {
    tipo_pago_id: number;
    monto: string;
    efectivo_recibido?: string;
}

export interface VentaCafeteriaFormData {
    sesion_pos_id: number;
    cliente?: number | null;
    observaciones?: string;
    items: ItemVenta[];
    pago?: PagoVenta;
    tipo_comprobante?: 'factura' | 'recibo';
    descuento?: number; // ← campo descuento
}

export const fetchVentasCafeteria = async (params?: {
    sesion_pos_id?: number;
}): Promise<VentaCafeteria[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(API_URL, { params });
        return response.data;
    });
};

export const fetchVentaCafeteria = async (id: number): Promise<VentaCafeteria> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}${id}/`);
        return response.data;
    });
};

/**
 * Venta de cafetería desde la caja explícita. El backend exige el grant del
 * operador (``X-POS-Operator-Token``) y atribuye el asiento de caja a ese
 * operador; sin grant no se llama al servidor.
 */
export const createVentaCafeteria = async (
    data: VentaCafeteriaFormData,
    operadorToken: string | null | undefined,
): Promise<VentaCafeteria> => {
    const config = operatorHeaders(requireOperatorToken(operadorToken), { 'Content-Type': 'application/json' });
    return handleRequestWithAuth(async () => {
        const payload: any = {
            sesion_pos_id: data.sesion_pos_id,
            items: data.items,
            observaciones: data.observaciones || '',
        };
        if (data.cliente) payload.cliente = data.cliente;
        if (data.pago) payload.pago = data.pago;
        if (data.tipo_comprobante) payload.tipo_comprobante = data.tipo_comprobante;
        // ✅ AGREGAR DESCUENTO
        if (data.descuento !== undefined && data.descuento !== null) {
            payload.descuento = data.descuento;
        }

        const response = await axiosInstance.post(API_URL, payload, config);
        return response.data;
    });
};

// Las ventas de cafetería son historial append-only: el backend solo admite
// GET/POST (405 para PUT/PATCH/DELETE), por eso aquí no existen escritores de
// edición ni borrado.

export interface FacturacionVenta { factura: { id: number; numero_factura: number | null; estado: string; mensajes_siat?: { descripcion?: string; codigo?: string | number }[] } | null; detalle: string; }
export const fetchFacturacionVenta = (id: number): Promise<FacturacionVenta> =>
    handleRequestWithAuth(async () => (await axiosInstance.get(`${API_URL}${id}/facturacion/`)).data);
export const emitirFacturaVenta = (id: number, token?: string | null): Promise<FacturacionVenta> =>
    handleRequestWithAuth(async () => (await axiosInstance.post(`${API_URL}${id}/emitir-factura/`, {}, token ? operatorHeaders(token) : {})).data);
